// Customers list — paginated table + search + create/edit/delete actions.

import { useState } from 'react';
import {
  Button,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
  type TablePaginationConfig,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Customer } from '../features/customers/customers.api';
import { useCustomers, useDeleteCustomer } from '../features/customers/useCustomers';
import { CustomerFormModal } from '../features/customers/CustomerFormModal';

const { Title } = Typography;
const { Search } = Input;

export function CustomersListPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Customer | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, isFetching } = useCustomers({
    page,
    limit,
    search: search || undefined,
    sort: '-createdAt',
  });
  const deleteMutation = useDeleteCustomer();

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Đã xoá');
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(apiMessage ?? 'Không xoá được');
    }
  }

  const columns: ColumnsType<Customer> = [
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    { title: 'Liên hệ', dataIndex: 'contactName', key: 'contactName' },
    { title: 'Email', dataIndex: 'contactEmail', key: 'contactEmail' },
    { title: 'SĐT', dataIndex: 'contactPhone', key: 'contactPhone' },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('vi-VN'),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Xoá customer này?"
            description="Customer còn license sẽ không xoá được."
            okText="Xoá"
            cancelText="Huỷ"
            onConfirm={() => void handleDelete(row.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize: limit,
    total: data?.meta.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Customers</Title>
        <Space>
          <Search
            placeholder="Tìm theo tên / email"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
            style={{ width: 280 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            Tạo customer
          </Button>
        </Space>
      </Space>

      <Table<Customer>
        rowKey="id"
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading || isFetching}
        pagination={pagination}
        onChange={(pag) => {
          if (pag.current && pag.current !== page) setPage(pag.current);
          if (pag.pageSize && pag.pageSize !== limit) setLimit(pag.pageSize);
        }}
      />

      <CustomerFormModal
        open={modalOpen}
        editing={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </>
  );
}
